import { useTranslation } from 'react-i18next'
import Alert from '@/shared/components/Alert/Alert'
import Select from '@/shared/components/Select/Select'
import Skeleton from '@/shared/components/Skeleton/Skeleton'
import { OptionType } from '@/shared/api/BaseAPI.model'
import { selectValueType } from '@/shared/components/DynamicForm/DynamicForm'
import { useRestaurantRooms } from '../../hooks/useRestaurantRooms'
import { roomPlanFrom } from '../../utils/roomPlan'
import type { RoomPlan } from '../../schema/elements'

interface RoomPickerProps {
  label: string
  /** The rooms already on the page, as the document holds them. */
  value: RoomPlan[]
  onChange: (rooms: RoomPlan[]) => void
}

/**
 * Which of the venue's rooms go on the page.
 *
 * One control for both tabs: the template asks the same question the builder's
 * panel does, and the answer is copied out of the planner the same way in both.
 *
 * The rooms are COPIED and not referenced, because a published page has no
 * session to fetch a plan with, and a table dragged across the planner next
 * month must not redraw a page saved this month.
 */
function RoomPicker({ label, value, onChange }: RoomPickerProps) {
  const { t } = useTranslation()
  const { rooms, tables, isLoading } = useRestaurantRooms()

  if (isLoading) return <Skeleton height="3.5rem" />

  if (rooms.length === 0) {
    return (
      <Alert severity="warning" message={t('WEBSITE.PROPS.ROOM_NONE_DRAWN')} />
    )
  }

  const options: OptionType[] = rooms.map(room => ({
    value: room.id,
    label: room.name
  }))

  const chosen = options.filter(option =>
    value.some(room => room.id === option.value)
  )

  const handleChange = (next: selectValueType) => {
    const picked = Array.isArray(next) ? next : [next]
    const ids = new Set(picked.filter(Boolean).map(option => option!.value))
    /*
      Rebuilt from the planner's list rather than from what was clicked, so the
      rooms keep the order the planner shows them in. Taken in click order, a
      guest would read the venue in whatever sequence somebody happened to tick
      the boxes.
    */
    onChange(
      rooms
        .filter(room => ids.has(room.id))
        .map(room => roomPlanFrom(room, tables))
    )
  }

  return (
    <Select
      label={label}
      options={options}
      value={chosen}
      onChange={handleChange}
      isMutiSelect
      focused
      readonly
    />
  )
}

export default RoomPicker
